/**
 * Authentication Storage Sync Plugin
 *
 * Provides cross-tab synchronization for authentication state by listening
 * for localStorage changes from other tabs.
 *
 * Features:
 * - Listens for 'storage' events to detect changes from other tabs
 * - Synchronizes tokenExpiresAt across all tabs when one tab refreshes the token
 * - Synchronizes isRefreshing flag to prevent redundant refresh attempts
 * - Proactively re-fetches user data when tokenExpiresAt changes (indicating token refresh)
 * - Handles logout events from other tabs to clear state and redirect to login
 *
 * Note: The 'storage' event only fires when localStorage is changed by ANOTHER
 * window/tab, not the current one. This means changes made in the same tab
 * won't trigger this listener.
 */

import { createAuthStorageSyncHandler } from '~/utils/auth.utils'

export default defineNuxtPlugin(nuxtApp => {
  // Only run on client side
  if (!import.meta.client) {
    return
  }

  // Use $pinia from nuxtApp to ensure Pinia context is available
  const pinia = nuxtApp.$pinia
  const authStore = useAuthStore(pinia)

  // Create the storage event handler with callbacks that update the auth store
  const storageHandler = createAuthStorageSyncHandler({
    onTokenExpiresAtChange: (newValue: number | null, oldValue: number | null) => {
      // Update the store with the new token expiration
      authStore.tokenExpiresAt = newValue

      // If the tokenExpiresAt actually changed (not just set to same value)
      // and we are authenticated, proactively re-fetch user data
      // This ensures user info stays in sync when another tab refreshes the token
      if (newValue !== null && newValue !== oldValue && authStore.isAuthenticated) {
        // Re-fetch user data silently - don't throw errors to avoid disrupting the UI
        authStore.fetchUser().catch((error) => {
          console.error('Failed to sync user data from another tab:', error)
        })
      }
    },
    onRefreshingChange: (isRefreshing: boolean) => {
      authStore.isRefreshing = isRefreshing
    },
    onLogoutEvent: () => {
      // Clear state without calling backend (another tab already did that)
      authStore.clearState()
      // Navigate to login page
      navigateTo('/auth/login')
    },
  })

  // Register the storage event listener
  // Note: This listener is intentionally kept active for the lifetime of the app
  // to enable cross-tab synchronization. It will be automatically cleaned up
  // by the browser when the page is closed or refreshed.
  window.addEventListener('storage', storageHandler)

  // Provide cleanup function for manual removal if needed (e.g., for testing)
  return {
    provide: {
      authStorageSyncCleanup: () => {
        window.removeEventListener('storage', storageHandler)
      },
    },
  }
})
